import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createEpicorJob } from '../../services/epicorApi';
import { getInventorySummary, listInventoryWarehouses } from '../../services/inventoryApi';
import { PageHeader, Card, StatGrid, StatCard, Button, Badge } from '../ui';

const fmt = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toLocaleString() : (n ?? '—');
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

// Freshness thresholds for the "last synced" badge (minutes).
const FRESH_MINUTES = 30;
const STALE_MINUTES = 120;

const ageMinutes = (t) => (t ? (Date.now() - new Date(t).getTime()) / 60000 : null);

const FreshnessBadge = ({ at }) => {
  const mins = ageMinutes(at);
  if (mins == null) return <Badge variant="neutral">never synced</Badge>;
  if (mins <= FRESH_MINUTES) return <Badge variant="success">fresh</Badge>;
  if (mins <= STALE_MINUTES) return <Badge variant="warning">aging</Badge>;
  return <Badge variant="danger">stale</Badge>;
};

const InventoryDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [runMsg, setRunMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, w] = await Promise.all([
        getInventorySummary(),
        listInventoryWarehouses(),
      ]);
      setSummary(s);
      setWarehouses(w || []);
    } catch (e) {
      // Non-fatal; panels stay stale.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const runSync = async (syncMode) => {
    setRunMsg(null);
    try {
      const { job_id } = await createEpicorJob({
        companies: ['AU', 'NZ'],
        stage: 'inventory',
        sync_mode: syncMode,
      });
      setRunMsg({
        tone: 'ok',
        text: `Queued ${syncMode} inventory sync as Epicor job #${job_id}. Track it under Jobs / Job History (Epicor tab) or run it from Manual Syncs for live progress.`,
      });
    } catch (err) {
      setRunMsg({ tone: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  const companies = summary?.companies || [];
  const totSkus = companies.reduce((a, r) => a + Number(r.skus_tracked || 0), 0);
  const totOut = companies.reduce((a, r) => a + Number(r.out_of_stock || 0), 0);
  const totNeg = companies.reduce((a, r) => a + Number(r.negative_availability || 0), 0);
  const lastSynced = companies.reduce(
    (acc, r) => (r.last_synced_at && (!acc || r.last_synced_at > acc) ? r.last_synced_at : acc),
    null,
  );
  const anyStale = ageMinutes(lastSynced) == null || ageMinutes(lastSynced) > STALE_MINUTES;

  return (
    <div className="sync-page inventory-dashboard">
      <PageHeader
        title="Inventory"
        sub="Per-SKU, per-warehouse stock snapshots pulled from Epicor (Erp.PartWhse), with live open sales-order demand from Erp.OrderRel. Available to sell counts firm unshipped orders (incl. held) as committed. Frequent changes-only syncs keep availability current; a periodic full sync reconciles everything. Nothing here writes to Epicor or BigCommerce — the B2B site consumes stock via the middleware availability API."
        actions={<Button variant="secondary" to="/inventory/stock">Stock levels</Button>}
      />

      {anyStale && companies.length > 0 && (
        <div className="banner warning" role="alert">
          Inventory data is stale — the last sync was {fmtTime(lastSynced)}. Run a changes sync
          below, or check the scheduler and worker health if syncs should be running automatically.
        </div>
      )}

      <StatGrid>
        <StatCard label="SKU / warehouse rows" value={fmt(totSkus)} tone="primary" />
        <StatCard label="Warehouses" value={fmt(warehouses.length)} />
        <StatCard
          label="Out of stock"
          value={fmt(totOut)}
          sub="available to sell ≤ 0"
          tone={totOut > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="Negative availability"
          value={fmt(totNeg)}
          sub="drift signal (raw availability < 0)"
          tone={totNeg > 0 ? 'warning' : undefined}
        />
      </StatGrid>

      <Card
        title="Run a sync"
        desc="Each button enqueues an Epicor job; the worker does the rest. Changes sync pulls only rows whose Epicor SysRevID moved past the watermark. Full sync re-pulls everything and reconciles removals."
      >
        <div className="checkbox-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => runSync('incremental')}>
            Run changes sync
          </Button>
          <Button variant="secondary" onClick={() => runSync('full')}>
            Run full sync
          </Button>
        </div>
        {runMsg && (
          <p className={runMsg.tone === 'ok' ? 'muted' : 'error-text'} style={{ marginTop: 8 }}>
            {runMsg.text}
          </p>
        )}
      </Card>

      <section className="card">
        <h3 className="section-h">Per-company status</h3>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>SKU/warehouse rows</th>
              <th>Warehouses</th>
              <th>Out of stock</th>
              <th>Negative availability</th>
              <th>Last synced</th>
              <th>Freshness</th>
            </tr>
          </thead>
          <tbody>
            {!companies.length && (
              <tr><td colSpan={7} className="muted">No inventory synced yet — run a full sync above.</td></tr>
            )}
            {companies.map((r) => (
              <tr key={r.company}>
                <td><strong>{r.company}</strong></td>
                <td>{fmt(r.skus_tracked)}</td>
                <td>{fmt(r.warehouses)}</td>
                <td>{fmt(r.out_of_stock)}</td>
                <td>{fmt(r.negative_availability)}</td>
                <td>{fmtTime(r.last_synced_at)}</td>
                <td><FreshnessBadge at={r.last_synced_at} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h3 className="section-h">Sync watermarks</h3>
        <p className="muted">
          The SysRevID watermark drives changes-only syncs: only rows whose rowversion exceeds it
          are pulled. It advances on every successful full or changes sync.
        </p>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Watermark (SysRevID)</th>
              <th>Last changes sync</th>
              <th>Last full sync</th>
            </tr>
          </thead>
          <tbody>
            {!summary?.watermarks?.length && (
              <tr><td colSpan={4} className="muted">No watermark yet — run a full sync first.</td></tr>
            )}
            {(summary?.watermarks || []).map((w) => (
              <tr key={w.company}>
                <td><strong>{w.company}</strong></td>
                <td>{fmt(w.last_sys_rev_id)}</td>
                <td>{fmtTime(w.last_incremental_sync_at)}</td>
                <td>{fmtTime(w.last_full_sync_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {summary?.history && (
          <p className="muted" style={{ marginTop: 8 }}>
            {fmt(summary.history.history_rows)} change-history rows recorded (90-day retention).
            Last stock movement: {fmtTime(summary.history.last_change_at)}.
          </p>
        )}
      </section>

      <section className="card">
        <h3 className="section-h">Warehouses</h3>
        <p className="muted">
          {warehouses.length
            ? `${warehouses.length} warehouses, ${warehouses.filter((w) => w.is_enabled !== false).length} in use.`
            : 'No warehouses synced yet.'}{' '}
          Manage which warehouses are in use (and see per-warehouse stock counts) on the{' '}
          <Link to="/configuration?tab=warehouses">Configuration &rsaquo; Warehouses</Link> page — disabled warehouses are
          excluded from the B2B availability API, the counts above, and the default stock-levels
          view.
        </p>
      </section>
    </div>
  );
};

export default InventoryDashboard;
